<template>
  <ak-modal-body class="overflow-visible">
    <ak-modal-title class="color-action text-breakout">{{
      title
    }}</ak-modal-title>

    <template v-if="loading">
      <div class="one flex flex--center flex--justify-center">
        <ak-loading-spinner />
      </div>
    </template>

    <template v-if="!loading">
      <ak-notification type="warning" class="mb" :closable="false">
        Please be aware that by cloning this existing page/form you may miss out
        on new features only available to newly created pages/forms.
      </ak-notification>

      <ak-notification
        v-if="modalType === 'move'"
        type="info"
        class="mb"
        :closable="false"
      >
        <ul>
          <li>
            This operation will clone your page to another campaign. Your Leads
            will not move over as part of this operation.
          </li>
          <li>
            Please update any automation workflows that have been configured for
            this app.
          </li>
        </ul>
      </ak-notification>

      <ak-field :label="titleLabel" required>
        <ak-input
          v-model="clonedTitle"
          required
          :maxlength="100"
          :custom-rules="customValidationRules"
          @blur="fixAppTitle"
        />
      </ak-field>

      <template v-if="display('show', 'developer,superadmin')">
        <ak-field label="Agency to clone to" required>
          <ak-select
            track-by="_id"
            label="name"
            :model-value="selectedAgency"
            :options="agencies"
            required
            max-height="200"
            @select="selectAgency"
          />
        </ak-field>
      </template>

      <template v-if="display('show', 'developer,superadmin,agencyadmin')">
        <ak-field label="Account to clone to" required>
          <ak-select
            track-by="_id"
            label="name"
            :model-value="selectedAccount"
            :options="accounts"
            required
            max-height="200"
            @select="selectAccount"
          />
        </ak-field>
      </template>

      <ak-field label="Campaign to clone to" required>
        <ak-select
          track-by="_id"
          label="title"
          :model-value="selectedCampaign"
          :options="campaigns"
          required
          max-height="200"
          @select="selectCampaign"
        />
      </ak-field>

      <ak-pack>
        <ak-pack-item grow>
          <ak-field label="Start Date">
            <ak-date-picker
              v-model="computedModel.app.startDate"
              :config="startDateConfig"
            />
          </ak-field>
        </ak-pack-item>
        <ak-pack-item grow>
          <ak-field label="End Date">
            <ak-date-picker
              v-model="computedModel.app.endDate"
              :config="endDateConfig"
            />
          </ak-field>
        </ak-pack-item>
      </ak-pack>

      <template
        v-if="
          display('show', 'developer,superadmin,agencyadmin,agencyuser') &&
          computedModel.app.type === 'promo'
        "
      >
        <ak-box background="grey-lightest">
          <ak-field>
            <ak-checkbox v-model="computedModel.app.hidden" label>
              <strong>Hidden Build</strong>
              <br />
              Hide this page from account users until it's ready for them to see
              in all its glory
            </ak-checkbox>
          </ak-field>
        </ak-box>
      </template>
    </template>
  </ak-modal-body>

  <ak-modal-footer>
    <ak-modal-button type="submit">{{
      capitalizeFirstLetter(modalType)
    }}</ak-modal-button>
  </ak-modal-footer>
</template>

<script>
import ModalBody from '@akerolabs/akero-vue/src/components/modal/ModalBody.vue'
import ModalTitle from '@akerolabs/akero-vue/src/components/modal/ModalTitle.vue'
import ModalFooter from '@akerolabs/akero-vue/src/components/modal/ModalFooter.vue'
import Notification from '@akerolabs/akero-vue/src/components/notification/Notification.vue'
import LoadingSpinner from '@akerolabs/akero-vue/src/components/loading/LoadingSpinner.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Checkbox from '@akerolabs/akero-vue/src/components/checkbox/Checkbox.vue'
import DatePicker from '@akerolabs/akero-vue/src/components/datepicker/DatePicker.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import date from '@akerolabs/akero-vue/src/utils/date.js'
import * as request from '@akerolabs/akero-vue/src/utils/request.js'
import {
  userObj,
  display,
  userLevelIsHigerThan,
} from '@akerolabs/akero-vue/src/services/users.js'
import { accountObj } from '@akerolabs/akero-vue/src/services/accounts.js'
import { agencyObj } from '@akerolabs/akero-vue/src/services/agency.js'

import {
  isObject,
  capitalizeFirstLetter,
} from '@akerolabs/akero-vue/src/utils/helpers'

import { LocalisationFactory } from '@akerolabs/akero-vue/src/services/localisation'
import { uniqueValidtion } from '../../../utils/validationRules.js'

import { defineComponent, computed, ref, onMounted } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-apps-list-clone-modal />
   */
  name: 'AppsListCloneModal',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [ModalBody.name]: ModalBody,
    [ModalTitle.name]: ModalTitle,
    [ModalFooter.name]: ModalFooter,
    [Notification.name]: Notification,
    [Select.name]: Select,
    [Input.name]: Input,
    [DatePicker.name]: DatePicker,
    [Field.name]: Field,
    [LoadingSpinner.name]: LoadingSpinner,
    [Checkbox.name]: Checkbox,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    title: {
      type: String,
      default: undefined,
    },

    modalType: {
      type: String,
      default: 'clone',
    },

    appType: {
      type: String,
      default: 'page',
    },

    modelValue: {
      default: () => ({}),
      type: Object,
    },
  },

  emits: ['update:modelValue'],

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const localisation = LocalisationFactory()
    const dateFormat = 'YYYY-MM-DD' // for the limits only
    const upperLimit = date().startOf('Day').add(10, 'years').format(dateFormat)
    const lowerLimit = date()
      .startOf('Day')
      .subtract(10, 'years')
      .format(dateFormat)

    const loading = ref(true)
    const agencies = ref([])
    const accounts = ref([])
    const campaigns = ref([])
    const selectedAgency = ref(undefined)
    const selectedAccount = ref(undefined)
    const selectedCampaign = ref(undefined)
    const maxStartDate = ref(props.modelValue.app.endDate || upperLimit)
    const minEndDate = ref(props.modelValue.app.startDate || lowerLimit)
    const maxEndDate = ref(upperLimit)

    // This will be set to computedModel.value.app.title, the input did have
    // the computedModel property as the v-model however this caused a bug
    // with the maxlength validator and would not properly update the
    // v-model
    const clonedTitle = ref('')

    /**
     * @type {Object}
     * @description The app data that we want to clone, we are using a computed
     * value, as vue does not like changing props directly, we need to emit
     * the changes back to the modal window component
     */
    const computedModel = computed({
      get() {
        return props.modelValue
      },
      set(value) {
        emit('update:modelValue', value)
      },
    })

    /**
     * @type {String}
     * @description The label for the title field
     */
    const titleLabel = computed(
      () => `Enter a new ${props.appType.toLowerCase()} name`,
    )

    /**
     * @type {Array}
     * @description These are the custom validation rules for the title
     * input, we are going to validation a slug that the API would
     * generate when cloning an app.
     */
    const customValidationRules = ref([
      uniqueValidtion({
        message: 'Name has already been used',
        type: props.modelValue.app.type,
        account: () =>
          userLevelIsHigerThan('agencyadmin')
            ? selectedAccount.value._id
            : accountObj()._id,
        agency: () =>
          userLevelIsHigerThan('superadmin')
            ? selectedAgency.value._id
            : agencyObj()._id,
        property: 'slug',
        slugify: true,
      }),
    ])

    /**
     * @type {Object}
     * @description Configuration for the start date picker field
     */
    const startDateConfig = computed(() => ({
      maxDate: maxStartDate.value,
      altInput: true,
      altFormat: localisation.getLocalisationPreferences().flatPickrFormat,
      dateFormat: 'Z',
    }))

    /**
     * @type {Object}
     * @description Configuration for the end date picker field
     */
    const endDateConfig = computed(() => ({
      minDate: minEndDate.value,
      maxDate: maxEndDate.value,
      altInput: true,
      altFormat: localisation.getLocalisationPreferences().flatPickrFormat,
      dateFormat: 'Z',
    }))

    /**
     * @returns {Promise}
     * @description Fetches the agencies data from the API to populate
     * the agency dropdown
     */
    const fetchAgencies = async () => {
      const { data: res } = await request.get('/agencies', {
        params: {
          per_page: 0,
        },
      })

      if (!selectedAgency.value) {
        const selected = res.data.find(
          (i) => i._id === computedModel.value.app.agency,
        )
        if (isObject(selected)) {
          selectedAgency.value = selected
        }
      }

      agencies.value = res.data
    }

    /**
     * @returns {Promise}
     * @description Fetches the accounts data from the API to populate
     * the accounts dropdown
     */
    const fetchAccounts = async () => {
      accounts.value = []

      const agency = isObject(selectedAgency.value)
        ? selectedAgency.value._id
        : agencyObj()._id

      const { data: res } = await request.get('/accounts', {
        params: {
          agency,
          overrideAccess: true,
          per_page: 0,
        },
      })

      if (!selectedAccount.value) {
        const selected = res.data.find(
          (i) => i._id === computedModel.value.app.account,
        )
        if (isObject(selected)) {
          selectedAccount.value = selected
        }
      }

      accounts.value = res.data
    }

    /**
     * @returns {Promise}
     * @description Fetches the campaigns data from the API to populate
     * the camapigns dropdown
     */
    const fetchCampaigns = async () => {
      campaigns.value = []

      const agency = isObject(selectedAgency.value)
        ? selectedAgency.value._id
        : agencyObj()._id
      const account = isObject(selectedAccount.value)
        ? selectedAccount.value._id
        : accountObj()._id

      const { data: res } = await request.get('/campaigns', {
        params: {
          agency,
          account,
          overrideAccess: true,
          per_page: 0,
        },
      })

      if (!selectedCampaign.value) {
        const selected = res.data.find(
          (i) => i._id === computedModel.value.app.campaign,
        )
        if (isObject(selected)) {
          selectedCampaign.value = selected
        }
      }

      campaigns.value = res.data
    }

    /**
     * @returns {Promise}
     * @description Fetches the required data for the modal to populate
     * all of the dropdowns correctly
     */
    const fetchData = async () => {
      if (display('show', 'developer,superadmin')) {
        await fetchAgencies()
      }
      if (display('show', 'developer,superadmin,agencyadmin')) {
        await fetchAccounts()
      }
      await fetchCampaigns()
      loading.value = false
    }

    /**
     * @param {Object} selected - the selected agency
     * @returns {Promise}
     * @description Sets the agency onto the app object, and fetches the
     * accounts for the agency that was selected to keep the accounts
     * select menu updated
     */
    const selectAgency = async (selected) => {
      computedModel.value.app.agency = selected._id
      selectedAgency.value = selected
      selectedAccount.value = undefined
      selectedCampaign.value = undefined
      campaigns.value = []
      await fetchAccounts()
    }

    /**
     * @param {Object} selected - the selected account
     * @returns {Promise}
     * @description Sets the account onto the app object, and fetches the
     * campaigns for the account that was selected to keep the camapigns
     * select menu updated
     */
    const selectAccount = async (selected) => {
      computedModel.value.app.account = selected._id
      computedModel.value.app.accountName = selected.name
      selectedAccount.value = selected
      selectedCampaign.value = undefined
      await fetchCampaigns()
    }

    /**
     * @param {Object} selected - the selected campaign
     * @returns {Promise}
     * @description Sets the campaign onto the app object, this will also set
     * the required campaign labels ect onto the app object
     */
    const selectCampaign = async (selected) => {
      computedModel.value.app.campaign = selected._id
      computedModel.value.app.campaignLabel = selected.title
      computedModel.value.app.campaignLabelLower = selected.title.toLowerCase()
      selectedCampaign.value = selected
    }

    /**
     * @description Keeps the fbConfig appName property updated when
     * the app title changes.
     */
    const fixAppTitle = () => {
      computedModel.value.app.titleLower = clonedTitle.value.toLowerCase()
      computedModel.value.app.fbConfig.appName = clonedTitle.value
      computedModel.value.app.title = clonedTitle.value
    }

    onMounted(async () => {
      const user = userObj()

      computedModel.value.app.cloned = true
      computedModel.value.app.clonedBy = user._id
      computedModel.value.app.clonedOn = Date.now()
      computedModel.value.app.clonedFrom = computedModel.value.app._id
      computedModel.value.app.clonedFromName = computedModel.value.app.title
      computedModel.value.app.automation = { autoresponse: { enabled: false } }
      computedModel.value.app.test = { until: Date.now(), enabled: false }
      computedModel.value.app.usedFields = []
      computedModel.value.app.title = ''
      computedModel.value.app.publishedStatus = false
      computedModel.value.app.publishedMicro = false
      computedModel.value.app.publishedFb = false
      computedModel.value.app.embedded = false
      computedModel.value.app.deleted = false
      computedModel.value.app.expires = null
      computedModel.value.app.properties = {}
      computedModel.value.app.publishedTo = []
      computedModel.value.app.publishedId = ''
      computedModel.value.app.appPoolId = null
      computedModel.value.app.appPoolSecret = null
      computedModel.value.app.leadsCount = 0
      computedModel.value.app.customSlug = null
      computedModel.value.app.experiments = []

      delete computedModel.value.app._id
      delete computedModel.value.app.id
      delete computedModel.value.app.__v
      delete computedModel.value.app.createdBy
      delete computedModel.value.app.createdOn
      delete computedModel.value.app.updatedBy
      delete computedModel.value.app.updatedOn
      delete computedModel.value.app.publishedBy
      delete computedModel.value.app.publishedOn
      delete computedModel.value.app.lastPublishOn
      delete computedModel.value.app.unpublishedBy
      delete computedModel.value.app.unpublishedOn
      delete computedModel.value.app.preview
      delete computedModel.value.app.slug
      delete computedModel.value.app.cachedMarkup

      await fetchData()
    })

    return {
      customValidationRules,
      capitalizeFirstLetter,
      computedModel,
      titleLabel,
      upperLimit,
      lowerLimit,
      maxStartDate,
      minEndDate,
      maxEndDate,
      display,
      loading,
      agencies,
      accounts,
      campaigns,
      selectedAgency,
      selectedAccount,
      selectedCampaign,
      selectAgency,
      selectAccount,
      selectCampaign,
      startDateConfig,
      endDateConfig,
      fixAppTitle,
      clonedTitle,
    }
  },
})
</script>
<style lang="scss">
@import '@akerolabs/akero-vue/src/scss/_settings.scss';
</style>
